import { defineStore } from 'pinia'
import { useBlogStore, useGamesStore, useWorkStore } from '#imports'
import type { AppTheme, AppView } from '~/types'

import type {
  Site,
  MenuItemRecord,
  MenuCtaRecord,
  FooterLinkRecord,
  ReelRecord,
  FooterMenuItemRecord,
  TeamMemberRecord
} from '~/types/datocms'

import { querySite } from '~/graphql/site'

export interface AppState {
  _site: Site
  allMenuCtas: MenuCtaRecord[]
  allMenuItems: MenuItemRecord[]
  allFooterMenuItems: FooterMenuItemRecord[]
  allFooterLinks: FooterLinkRecord[]
  allReels: ReelRecord[]
  allTeamMembers: TeamMemberRecord[]
  isLoading: boolean
  view: AppView | undefined
  theme: AppTheme | undefined
}

export const useAppStore = defineStore('AppStore', {
  state: (): AppState => {
    return {
      _site: {} as Site,
      allMenuCtas: [],
      allMenuItems: [],
      allFooterMenuItems: [],
      allFooterLinks: [],
      allReels: [],
      allTeamMembers: [],
      isLoading: true,
      view: undefined,
      theme: undefined
    }
  },
  actions: {
    async fetchData() {
      const blogStore = useBlogStore()
      const workStore = useWorkStore()
      const gamesStore = useGamesStore()

      this.setLoading(true)

      const { data, error } = await useDatoQuery(querySite)

      if (error.value) {
        console.error(error.value)
      }

      if (data.value) {
        this._site = data.value._site as Site
        this.allMenuCtas = data.value.allMenuCtas as MenuCtaRecord[]
        this.allMenuItems = data.value.allMenuItems as MenuItemRecord[]
        this.allFooterMenuItems = data.value.allFooterMenuItems as FooterMenuItemRecord[]
        this.allFooterLinks = data.value.allFooterLinks as FooterLinkRecord[]
        this.allReels = data.value.allReels as ReelRecord[]
        this.allTeamMembers = data.value.allTeamMembers as TeamMemberRecord[]
        blogStore.pushCategories(data.value.allCategories as CategoryRecord[])
        workStore.pushCategories(data.value.allWorkCategories as WorkCategoryRecord[])
        gamesStore.pushCategories(data.value.allGameCategories as GameCategoryRecord[])
      }
      this.setLoading(false)
    },
    async updateData() {
      const blogStore = useBlogStore()
      const workStore = useWorkStore()
      const gamesStore = useGamesStore()

      this.setLoading(true)

      const { data, error } = await useDatoQuery(querySite)

      if (error.value) {
        console.error(error.value)
      }
      if (data.value) {
        this._site = data.value._site as Site
        this.allMenuCtas = data.value.allMenuCtas as MenuCtaRecord[]
        this.allMenuItems = data.value.allMenuItems as MenuItemRecord[]
        this.allFooterLinks = data.value.allFooterLinks as FooterLinkRecord[]
        this.allTeamMembers = data.value.allTeamMembers as TeamMemberRecord[]
        blogStore.pushCategories(data.value.allCategories as CategoryRecord[])
        workStore.pushCategories(data.value.allWorkCategories as WorkCategoryRecord[])
        gamesStore.pushCategories(data.value.allGameCategories as GameCategoryRecord[])
      }
      this.setLoading(false)
    },
    setLoading(loading: boolean) {
      this.isLoading = loading
    },
    setTheme(theme: AppTheme) {
      this.theme = theme
    },
    setView(view: AppView) {
      this.view = view
    }
  }
})
